/** Presentation options for website LiB banners (parity with Home BannerSection.cfg). */

import { normalizeBannerImageFrame } from "./templateBannerUtils";
import {
  IMAGE_FRAME_ASPECT,
  type ImageFrameShape,
} from "./websiteImageFrameAspect";

export type WebsiteLibBannerOptions = {
  style: string;
  subtitle: string;
  showEyebrow: boolean;
  eyebrow: string;
  showSubtitle: boolean;
  showCta: boolean;
  bgTone: "bg" | "surface" | "accent";
  /** Cover zoom. 1 fills the frame; higher zooms in. */
  imageZoom: number;
  /** Cover focal point, 0–100. */
  imageX: number;
  imageY: number;
};

export const DEFAULT_WEBSITE_LIB_BANNER_OPTIONS: WebsiteLibBannerOptions = {
  style: "overlay-left",
  subtitle: "",
  showEyebrow: false,
  eyebrow: "",
  showSubtitle: true,
  showCta: true,
  bgTone: "bg",
  imageZoom: 1,
  imageX: 50,
  imageY: 50,
};

/** Current banner style keys shown in the inspector. */
export const BANNER_STYLE_OPTIONS = [
  "overlay-left",
  "contained",
  "ribbon",
  "overlay-center",
] as const;

/** Human labels for banner style pills. */
export const BANNER_STYLE_LABELS: Record<string, string> = {
  "overlay-left": "Overlay left",
  contained: "Contained",
  ribbon: "Ribbon",
  "overlay-center": "Overlay center",
  // Legacy (migrated on read)
  fullbleed: "Overlay left",
  split: "Contained",
  colored: "Contained",
};

/**
 * Migrate legacy banner styles to the current set.
 * fullbleed → overlay-left, split → contained, colored → contained.
 */
export function normalizeBannerStyle(style: unknown): string {
  const raw = String(style || "").trim().toLowerCase();
  if (
    raw === "fullbleed" ||
    raw === "overlay left" ||
    raw === "overlay-left" ||
    raw === "overlayleft"
  ) {
    return "overlay-left";
  }
  if (raw === "split" || raw === "contained" || raw === "colored") {
    return "contained";
  }
  if (raw === "ribbon") return "ribbon";
  if (
    raw === "overlay-center" ||
    raw === "overlay center" ||
    raw === "overlaycenter"
  ) {
    return "overlay-center";
  }
  if ((BANNER_STYLE_OPTIONS as readonly string[]).includes(raw)) return raw;
  return DEFAULT_WEBSITE_LIB_BANNER_OPTIONS.style;
}

/** Label for inspector pills / SettingRow. */
export function bannerStyleLabel(style: unknown): string {
  const key = normalizeBannerStyle(style);
  return BANNER_STYLE_LABELS[key] || BANNER_STYLE_LABELS["overlay-left"];
}

export const BANNER_BG_TONE_OPTIONS = ["bg", "surface", "accent"] as const;

/** True when `query` was written by website / LiB banner create (JSON opts). */
export function hasWebsiteLibBannerQuery(raw: unknown): boolean {
  if (typeof raw === "string") {
    const text = raw.trim();
    if (!text.startsWith("{")) return false;
    try {
      const value = JSON.parse(text);
      return (
        value != null &&
        typeof value === "object" &&
        ("style" in value ||
          "bgTone" in value ||
          "imageZoom" in value ||
          "imageX" in value)
      );
    } catch {
      return false;
    }
  }
  if (raw && typeof raw === "object") {
    const value = raw as Record<string, unknown>;
    return (
      "style" in value ||
      "bgTone" in value ||
      "imageZoom" in value ||
      "imageX" in value
    );
  }
  return false;
}

export type BannerCropDevice = "desktop" | "tablet" | "mobile";

/**
 * Crop-thumb aspect matching the live banner frame for each style.
 * Home overlay left/center follow `data-tpl-device` (16/9 → 4/3 → 3/4).
 * LiB stays 16/9 — live shells lock landscape under the 450px height cap.
 */
export function bannerStyleCropAspect(
  style: string,
  opts?: { device?: BannerCropDevice; linkInBio?: boolean },
): string {
  switch (normalizeBannerStyle(style)) {
    case "contained":
      return "5 / 4";
    case "overlay-center":
    case "overlay-left": {
      if (!opts?.linkInBio) {
        if (opts?.device === "mobile") return "3 / 4";
        if (opts?.device === "tablet") return "4 / 3";
      }
      return IMAGE_FRAME_ASPECT.landscape;
    }
    default:
      return IMAGE_FRAME_ASPECT.bannerLegacy;
  }
}

/** Crop mask matching live banner chrome (overlay styles are sharp edges). */
export function bannerStyleCropShape(style: string): ImageFrameShape {
  switch (normalizeBannerStyle(style)) {
    case "contained":
      return "rounded";
    case "overlay-center":
    case "overlay-left":
      return "rect";
    default:
      return "rounded";
  }
}

/** Recommend size string aligned with the crop aspect for each style. */
export function bannerStyleCropHint(
  style: string,
  opts?: { device?: BannerCropDevice; linkInBio?: boolean },
): string {
  switch (normalizeBannerStyle(style)) {
    case "contained":
      return "Recommended: 5∶4 (e.g. 1000 × 800)";
    case "overlay-center":
    case "overlay-left": {
      const aspect = bannerStyleCropAspect(style, opts);
      if (aspect === "3 / 4") return "Recommended: 3∶4 (e.g. 1080 × 1440)";
      if (aspect === "4 / 3") return "Recommended: 4∶3 (e.g. 1600 × 1200)";
      return "Recommended: 16∶9 (e.g. 1920 × 1080)";
    }
    default:
      return "Recommended: 657 × 350 px";
  }
}

export function parseWebsiteLibBannerOptions(
  raw: unknown,
): WebsiteLibBannerOptions {
  let parsed: Record<string, unknown> | null = null;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const value = JSON.parse(raw);
      if (value && typeof value === "object") {
        parsed = value as Record<string, unknown>;
      }
    } catch {
      parsed = null;
    }
  } else if (raw && typeof raw === "object") {
    parsed = raw as Record<string, unknown>;
  }

  if (!parsed) return { ...DEFAULT_WEBSITE_LIB_BANNER_OPTIONS };

  const style = normalizeBannerStyle(
    parsed.style || DEFAULT_WEBSITE_LIB_BANNER_OPTIONS.style,
  );
  const bgToneRaw = String(
    parsed.bgTone || DEFAULT_WEBSITE_LIB_BANNER_OPTIONS.bgTone,
  );
  const bgTone = (BANNER_BG_TONE_OPTIONS as readonly string[]).includes(bgToneRaw)
    ? (bgToneRaw as WebsiteLibBannerOptions["bgTone"])
    : DEFAULT_WEBSITE_LIB_BANNER_OPTIONS.bgTone;
  const frame = normalizeBannerImageFrame(parsed);

  return {
    style,
    subtitle: String(parsed.subtitle ?? ""),
    showEyebrow: parsed.showEyebrow === true,
    eyebrow: String(parsed.eyebrow ?? ""),
    showSubtitle: parsed.showSubtitle !== false,
    showCta: parsed.showCta !== false,
    bgTone,
    imageZoom: frame.zoom,
    imageX: frame.x,
    imageY: frame.y,
  };
}

export function serializeWebsiteLibBannerOptions(
  opts: WebsiteLibBannerOptions,
): string {
  return JSON.stringify({
    ...opts,
    style: normalizeBannerStyle(opts.style),
  });
}

/** Compact query JSON for LiB highlight cover zoom (imageZoom/X/Y only). */
export function serializeWebsiteLibImageFrame(frame: {
  zoom?: number;
  x?: number;
  y?: number;
  imageZoom?: number;
  imageX?: number;
  imageY?: number;
}): string {
  const next = normalizeBannerImageFrame(frame);
  return JSON.stringify({
    imageZoom: next.zoom,
    imageX: next.x,
    imageY: next.y,
  });
}
